import { useQuery, useMutation, useLazyQuery } from "@apollo/client";
import { ProfileProfessionEdit } from "./profile-profession-edit";
import {
  ApplicantUserProfessionalInput,
  Entity,
  ApplicantProfileProfessionEditContainerApplicantUserUpdateDocument,
  ApplicantProfileProfessionViewContainerApplicantUserDocument,
  ApplicantProfileProfessionEditContainerEntitiesDocument,
  ApplicantUserProfessional,
} from "../../../../generated";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import { Country } from "../../../../types/country";
import { lookupCountryQuery } from "./profile-profession-country-query";
import { lookupHealthProfessionQuery } from "./profile-profession-health-profession-query";
import { HealthProfession } from "../../../../types/healthProfession";
import { ComponentQueryLoader } from "../../../shared/component-query-loader";

interface ProfileProfessionEditContainerProps {
  data: {
    applicantId: string;
  };
}

export const ProfileProfessionEditContainer: React.FC<ProfileProfessionEditContainerProps> = (props) => {
  const navigate = useNavigate();

  const {
    data: professionData,
    loading: professionLoading,
    error: professionError,
  } = useQuery(ApplicantProfileProfessionViewContainerApplicantUserDocument, {
    variables: {
      applicantUserId: props.data.applicantId,
    },
  });

  const {
    data: countryData,
    error: countryError,
    loading: countryLoading,
  } = useQuery(lookupCountryQuery);

  const {
    data: healthData,
    error: healthProfessionError,
    loading: healthDataLoading,
  } = useQuery(lookupHealthProfessionQuery);

  // Query to get the entities data
  const {
    data: entityData,
    loading: entityLoading,
    error: entityError,
  } = useQuery(ApplicantProfileProfessionEditContainerEntitiesDocument);

  const [applicantUserUpdate] = useMutation(ApplicantProfileProfessionEditContainerApplicantUserUpdateDocument);

  const [getProfessionData] = useLazyQuery(ApplicantProfileProfessionViewContainerApplicantUserDocument);

const fetchProfessionData = async () => {
  try {
    const { data } = await getProfessionData({
      variables: {
        applicantUserId: props.data.applicantId,
      },
    });
    return data?.applicantUser?.personalInformation?.professional as ApplicantUserProfessional;
  } catch (error) {
    return null;
  }
};

  const handleSave = async (data: ApplicantUserProfessionalInput) => {
    try {
      const result = await applicantUserUpdate({
        variables: {
          input: {
            id: props.data.applicantId,
            applicantUserDetails: {
              personalInformation: {
                professional: {
                  citizenshipAtTimeEnteredMedicalInstitution:
                    data.citizenshipAtTimeEnteredMedicalInstitution,
                  healthProfession: data.healthProfession,
                  primaryEducationalInformation:
                    data.primaryEducationalInformation,
                },
              },
            },
          },
        },
      });
      if (result.data?.applicantUserUpdate.status.success) {
        message.success("Profession details updated");
        handleEdit();
      } else {
        message.error("Failed to update Profession details");
      }
    } catch (error) {
      message.error(`Error Updating Profession: ${JSON.stringify(error)}`);
    }
  };

  const filteredEntityData =
    entityData?.entities?.filter(
      (entity) => entity?.isIssuingInstitution === true
    ) || [];

  const handleEdit = () => {
    const currentPath = location.pathname;
    const newPath = currentPath.replace(/\/[^/]+$/, "/view");
    navigate(newPath);
  };

  return (
    <ComponentQueryLoader
      error={
        countryError || professionError || entityError || healthProfessionError
      }
      loading={countryLoading || professionLoading || entityLoading || healthDataLoading}
      hasData={!!professionData?.applicantUser?.personalInformation?.professional}
      hasDataComponent={
        <ProfileProfessionEdit
        professionalInformation={{
          citizenshipAtTimeEnteredMedicalInstitution: professionData?.applicantUser?.personalInformation?.professional?.citizenshipAtTimeEnteredMedicalInstitution || '',
          healthProfession: professionData?.applicantUser?.personalInformation?.professional?.healthProfession || '',
          primaryEducationalInformation: professionData?.applicantUser?.personalInformation?.professional?.primaryEducationalInformation || [],
        }}
          onCancel={handleEdit}
          countryData={
            countryData?.countries?.eventPayload?.countries as Country[]
          }
          healthProfessions={
            healthData?.healthProfessionsData
              ?.healthProfessions as HealthProfession[]
          }
          onSave={handleSave}
          fetchProfessionData={fetchProfessionData}
          entities={filteredEntityData as Entity[]}
        />
      }
    />
  );
};
